/* eslint-disable no-console */
import moment from 'moment';
import Cookies from 'js-cookie';
import { Toast } from 'service/toast';
import { menuItems } from 'Data/staticDatas';

export const getSafe = (fn, defaultValue = '') => {
  try {
    return fn();
  } catch (e) {
    return defaultValue;
  }
};

export const replaceEmpty = (val, defaultValue = null) => {
  if (val) return val;

  return defaultValue;
};

export const getCustomerTitle = (gender = '') => {
  const normalizedGender = String(gender || '')
    .trim()
    .toLowerCase();
  if (normalizedGender === 'female') return 'Ms./Mrs. ';
  if (normalizedGender === 'male') return 'Mr. ';
  return 'Mr./Mrs. ';
};

export const dateFormatter = (date, format = 'DD/MM/YYYY') => {
  if (!date) return '';

  const finalDate = moment(date).format(format);
  return finalDate === 'Invalid date' ? '' : finalDate;
};

export const convertDate = (date, inputFormat = '', format = 'DD/MM/YYYY') => {
  if (!date) return '';

  const finalDate = moment(date, inputFormat).format(format);
  return finalDate === 'Invalid date' ? '' : finalDate;
};

export const addCookie = (key, value, options = {}) => {
  Cookies.set(key, value, options);
};

export const removeCookie = (key, options = {}) => {
  Cookies.remove(key, options);
};

export const getCookie = (key) => Cookies.get(key);

export const getValidValue = (data, type = 'normal', replaceValue = null) => {
  if (type === 'select') return data.label && data.value ? data : replaceValue;

  if (typeof data === 'boolean') return data;

  if (Array.isArray(data) && data?.length === 0) return replaceValue;

  if (typeof data === 'object' && Object.keys(data || {})?.length === 0)
    return replaceValue;

  if (typeof data === 'undefined') return replaceValue;

  return data;
};

/**
 * Displays the short number count (eg:) 325K,2.4M,...etc
 *
 * @param {number | string} val the number which needs to be displayed for short count.
 * @returns {string} returns the short count (e.g) if 3000 is the input then it returns 3K as output
 */

export const countHelper = (val) => {
  let displayCount = '';

  if (Number.isNaN(val)) return displayCount;

  displayCount = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short'
  }).format(val);

  return displayCount;
};

/**
 * Extract the right error message from react-hook-form error object
 *
 * @param {object} errors Errors object from react-hook-form
 * @param {object} fieldArrayError  { index, fieldName: "date", arrayName: "callLogs", }
 * @param {string} name
 */

export const getFormErrorMessage = (errors, fieldArrayError, name) => {
  if (!name || !errors) return null;

  if (!fieldArrayError && name) {
    return errors[name];
  }

  return (
    !!errors[fieldArrayError.arrayName] &&
    errors[fieldArrayError.arrayName][fieldArrayError.index] &&
    typeof errors[fieldArrayError.arrayName][fieldArrayError.index][
      fieldArrayError.fieldName
    ] !== 'undefined' &&
    errors[fieldArrayError.arrayName][fieldArrayError.index][
      fieldArrayError.fieldName
    ]
  );
};

function fallbackCopyTextToClipboard(text, message) {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    const msg = successful ? 'successful' : 'unsuccessful';
    Toast({ type: 'success', message });
    console.log(`Fallback: Copying text command was ${msg}`);
  } catch (err) {
    // console.error('Fallback: Oops, unable to copy', err);
  }

  document.body.removeChild(textArea);
}

export function copyTextToClipboard(text, message) {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text, message);
    return;
  }
  navigator.clipboard.writeText(text).then(
    function () {
      Toast({ type: 'success', message });
    },
    function (err) {
      Toast({ type: 'error', message: err.message || 'Error' });
    }
  );
}

export const convertToValidObject = (obj) => {
  const currentObj = { ...obj };

  Object.keys(currentObj)
    .filter((key) => typeof currentObj[key] !== 'boolean')
    .forEach((key) => {
      if (!currentObj[key]) {
        delete currentObj[key];
      }
    });

  return { ...currentObj };
};

export const addressFormatter = (deliveryAddress) => {
  const { addressLine1, addressLine2, city, state, country, pincode } =
    deliveryAddress || {};

  const addressArray = [
    getValidValue(addressLine1),
    getValidValue(addressLine2),
    getValidValue(city),
    getValidValue(state),
    getValidValue(country)
  ].filter((data) => data !== getValidValue(undefined));

  return `${addressArray.join(' , ')} - ${pincode}`;
};

export const addPaymentDuration = (paymentData) => {
  const { responseTimestamp = 1649396506275, txnToken } = paymentData || {};

  if (responseTimestamp) {
    const isoTime = new Date(+responseTimestamp).toISOString();

    const endTime = moment(isoTime).add(15, 'minutes').toDate();
    addCookie('txnToken', txnToken, { expires: endTime });
    addCookie('responseTimestamp', responseTimestamp, { expires: endTime });
    addCookie('paymentData', JSON.stringify(paymentData), { expires: endTime });
  }
};

export const isPaymentDurationValid = () => {
  const responseTimestamp = getCookie('responseTimestamp');

  if (!responseTimestamp) return false;

  const isoTime = new Date(+responseTimestamp).toISOString();

  return moment(isoTime).add(15, 'minutes').toDate();
};

export const urlCombiner = (url) => {
  const protocol = ['h', 't', 't', 'p', 's'].join('');

  return `${protocol}://${url}`;
};

export const defaultValidator = (val, val2, defaultValue = '-') => {
  return val ? val2 : defaultValue;
};

export const ToastError = (err) => {
  return Toast({ type: 'error', message: err.length > 0 ? err : 'Error' });
};

export const generateQuery = (query) => {
  const params = new URLSearchParams();
  Object.keys(query).forEach((param) => {
    if (query[param]) {
      params.append(param, query[param]);
    }
  });
  return params.toString() ? `?${params.toString()}` : '';
};

export const renderNoData = (val, type) => {
  if (type === 'string') {
    if (val !== 'undefined' && val !== null && val !== 'NaN') {
      return val;
    }
    return '';
  }
  return val || '-';
};

// export const renderNoSelect = (val, labelProp, valueProp) => {
//   if (!val) {
//     return '';
//   }
//   return {
//     label: val?.[labelProp] || val || '',
//     value: val?.[valueProp] || val || ''
//   };
// };

export const getAccessToken = () => {
  return localStorage.getItem('token');
};

export const handleControlKeys = (e) => {
  const allowkeys =
    e.key === 'Backspace' ||
    e.key === 'Tab' ||
    e.ctrlKey ||
    e.key === 'ArrowLeft' ||
    e.key === 'ArrowRight';
  return allowkeys;
};

export const handleForbiddenCharacters = (e, type, min = 1, max = 250) => {
  const forbiddenChars = [
    '-',
    '+',
    '#',
    '$',
    '%',
    '^',
    '*',
    '!',
    '(',
    ')',
    '?',
    '/',
    ';',
    ',',
    '>',
    '<'
  ];
  if (handleControlKeys(e)) {
    return;
  }
  if (forbiddenChars.includes(e.key)) {
    e.preventDefault();
  } else if (type === 'number' && ['e', '@', '&'].includes(e.key)) {
    e.preventDefault();
  } else if (type === 'number' && !/\d/.test(e.key)) {
    e.preventDefault();
  }
  const currentValue = e.target.value + e.key;
  if (currentValue.length < min || currentValue.length > max) {
    e.preventDefault();
  }
};

export const handleDecimal = (event, min, max) => {
  if (handleControlKeys(event)) {
    return;
  }
  const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.'];
  if (!allowedKeys.includes(event.key)) {
    event.preventDefault();
    return;
  }
  const currentValue = event.target.value + event.key;
  const dotCount = (currentValue.match(/\./g) || []).length;
  if (dotCount > 1 || currentValue.length < min || currentValue.length > max) {
    event.preventDefault();
  }
};

export const handleDecimalValue = (event, minimumValue, maximumValue) => {
  const currentValue = Number(event.target.value + event.key);
  if (event.key === 'Backspace') {
    return;
  }
  // eslint-disable-next-line no-restricted-globals
  if (!/^\d*\.?\d{0,2}$/.test(currentValue)) {
    event.preventDefault();
    return;
  }
  const parsedValue = parseFloat(currentValue);
  if (parsedValue < minimumValue || parsedValue > maximumValue) {
    event.preventDefault();
  }
};

export const handleAlphabets = (event) => {
  if (handleControlKeys(event)) {
    return;
  }
  const keyCode = event.keyCode || event.which;
  if (
    (keyCode >= 65 && keyCode <= 90) ||
    keyCode === 32 ||
    (keyCode >= 97 && keyCode <= 122)
  ) {
    return;
  }
  event.preventDefault();
};

export const handleName = (event) => {
  if (handleControlKeys(event)) {
    return;
  }
  const keyCode = event.keyCode || event.which;
  if (
    (keyCode >= 65 && keyCode <= 90) ||
    keyCode === 32 ||
    (keyCode >= 97 && keyCode <= 122) ||
    keyCode === 190
  ) {
    return;
  }
  event.preventDefault();
};

export const handleSerialNumber15 = (
  index,
  _count,
  page,
  itemsPerPage = 15
) => {
  return (page - 1) * itemsPerPage + index + 1;
};

export const handleSerialNumber = (index, count, page) => {
  const isLastItem = index + 1 === count;
  let serialNumber = 0;
  if (page === 1) {
    serialNumber = index + 1;
  } else if (isLastItem && count === 10) {
    serialNumber = `${page}${0}`;
  } else {
    serialNumber = `${page - 1}${index + 1}`;
  }
  return serialNumber;
};

export const getUserRole = (userRole) => {
  const userData = JSON.parse(localStorage.getItem('userData'));
  const isExisting = userRole.some((role) => userData?.roles.includes(role));
  return isExisting;
};

// export const getMenuRoute = (userRoles) => {
//   // eslint-disable-next-line no-restricted-syntax
//   for (const menuItem of menuItems) {
//     if (menuItem.role.some((role) => userRoles.includes(role))) {
//       return menuItem.link;
//     }
//   }
//   return '/home/manage-products';
// };

export const getMenuRoute = (userRoles) => {
  // Helper function to find the route recursively
  const findRoute = (items) => {
    return items.find((item) => {
      // Check if user roles match the item's roles
      if (item.role.some((role) => userRoles.includes(role))) {
        return true;
      }

      // Check in subOptions, if available
      if (item.subOptions) {
        return findRoute(item.subOptions); // Recursively check subOptions
      }

      return false;
    });
  };

  // Find matching menu item
  const matchingItem = findRoute(menuItems);

  // Return the appropriate link or default route
  return (
    matchingItem?.link ||
    matchingItem?.subOptions?.[0]?.subLink ||
    '/home/manage-products'
  );
};
